import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { RankedListWidget } from '../../dashboard-basic-view/components/RankedListWidget/ranked-list-widget';
import { NoDataWidget } from '../../dashboard-basic-view/components/NoDataWidget/no-data-widget';
import { formatPercent } from '../../../reporting/components/portfolio-report';
import { ReportingPaths } from '../../../reporting/reporting.router';
import { PortfolioWidgetProps } from './portfolio-widget.types';

export function PortfolioEquityWidget({ data, isLoading }: PortfolioWidgetProps) {
  const { t } = useTranslation('widget');

  const buildings = useMemo(() => data?.buildings ?? [], [data]);

  const { equityRatio, topHighest, bottomLowest } = useMemo(() => {
    let equity = 0;
    let totalCapital = 0;

    const ranked = buildings
      .filter((b) => b.equityRatioPercent != null)
      .map((b) => ({ id: b.id, label: b.name, value: b.equityRatioPercent as number }));

    buildings.forEach((b) => {
      equity += b.financialEquity ?? 0;
      totalCapital += b.financialTotalCapital ?? 0;
    });

    const byValueDesc = [...ranked].sort((a, b) => b.value - a.value);

    return {
      equityRatio: totalCapital > 0 ? (equity / totalCapital) * 100 : null,
      topHighest: byValueDesc.slice(0, 3),
      bottomLowest: [...ranked].sort((a, b) => a.value - b.value).slice(0, 3),
    };
  }, [buildings]);

  if (!isLoading && topHighest.length === 0 && bottomLowest.length === 0)
    return <NoDataWidget title={t('portfolioEquity.title')} noDataText={t('portfolioEquity.noData')} />;

  return (
    <RankedListWidget
      to={ReportingPaths.FinancialOverview}
      title={t('portfolioEquity.title')}
      tooltip={t('portfolioEquity.tooltip')}
      value={formatPercent(equityRatio)}
      unit={t('portfolioEquity.unit')}
      rankUnit={t('portfolioEquity.rankUnit')}
      loading={isLoading || !data}
      highListTitle={t('portfolioEquity.topThreeHighest')}
      lowListTitle={t('portfolioEquity.bottomThreeLowest')}
      highListData={topHighest}
      lowListData={bottomLowest}
    />
  );
}
